import { useCallback, useState } from 'react';
import { Button, Typography } from '@mui/material';

import { EditBackgroundStyles } from './edit-background-styles';
import { EditFontStyles } from './edit-font-styles';
import { BackgroundStyles, Quiz, QuizFontStyles, QuizStyles } from 'types';
import { updateQuizStyles, upsertQuizStyles } from 'graphql/mutations';
import { UpsertQuizStyles, UpsertQuizStylesVariables } from 'graphql/mutations/types/UpsertQuizStyles';
import { UpdateQuizStyles, UpdateQuizStylesVariables } from 'graphql/mutations/types/UpdateQuizStyles';
import { ApolloError, useMutation } from '@apollo/client';
import { Spinner } from 'components/spinner';
import { useSnackbar } from 'components/snackbar';
import omit from 'lodash/omit';

interface Props {
    quiz: Quiz;
    onStylesChange: () => void;
}

/*
  TODO
  - consider adding a slide preview on top taking into account all selected unsaved styles 
 */

export const EditQuizStyles = (props: Props) => {
    const snackbar = useSnackbar();
    const [saving, setSaving] = useState(false);
    const [titleFontStyles, setTitleFontStyles] = useState<QuizFontStyles>(props.quiz.styles?.titlePageStyles?.title ?? {});
    const [titleBackgroudStyles, setTitleBackgroundStyles] = useState<BackgroundStyles>(
        props.quiz.styles?.titlePageStyles ? omit(props.quiz.styles?.titlePageStyles, 'title') : {});
    const [questionFontStyles, setQuestionFontStyles] = useState<QuizFontStyles>(props.quiz.styles?.fontStyles ?? {});
    const [questionBackgroudStyles, setQuestionBackgroundStyles] = useState<BackgroundStyles>(props.quiz.styles?.backgroundStyles ?? {});
    const [uploadTitlePageBackgroundImage, setUploadTitlePageBackgroundImage] = useState<{ upload: () => Promise<string | undefined> }>();
    const [uploadSlideBackgroundImage, setUploadSlideBackgroundImage] = useState<{ upload: () => Promise<string | undefined> }>();

    const onQuizUpdateError = (error: ApolloError) => {
        setSaving(false);
        console.error('Error updating quiz ', error);
        snackbar.error('Error updating quiz');
    };

    const onQuizUpdated = (data: UpsertQuizStyles | UpdateQuizStyles) => {
        setSaving(false);
        props.onStylesChange();
        console.info('Quiz styles successfully saved');
        snackbar.success('Quiz styles successfully saved');
    };

    const [upsertQuizStylesMutation] = useMutation<UpsertQuizStyles, UpsertQuizStylesVariables>(upsertQuizStyles, {
        onCompleted: onQuizUpdated,
        onError: onQuizUpdateError
    });

    const [updateQuizStylesMutation] = useMutation<UpdateQuizStyles, UpdateQuizStylesVariables>(updateQuizStyles, {
        onCompleted: onQuizUpdated,
        onError: onQuizUpdateError
    });

    const onTitleFontStylesChange = (fontStyles: QuizFontStyles) => {
        setTitleFontStyles(fontStyles);
    };

    const onTitleBackgroundStylesChange = (backgroundStyles: BackgroundStyles): void => {
        setTitleBackgroundStyles(backgroundStyles);
    };

    const onFontStylesChange = (fontStyles: QuizFontStyles) => {
        setQuestionFontStyles(fontStyles);
    };

    const onBackgroundStylesChange = (backgroundStyles: BackgroundStyles): void => {
        setQuestionBackgroundStyles(backgroundStyles);
    };

    const onSaveStyles = async () => {
        setSaving(true);

        const cTitleBckStyles = { ...titleBackgroudStyles };
        const cSlideBckStyles = { ...questionBackgroudStyles };

        if (uploadTitlePageBackgroundImage) {
            const titleBckImageUrl = await uploadTitlePageBackgroundImage.upload();
            if (!titleBckImageUrl) {
                snackbar.error('Error uploading title page background image!');
            }
            cTitleBckStyles.backgroundImageUrl = titleBckImageUrl!;
        }

        if (uploadSlideBackgroundImage) {
            const slideBckImageUrl = await uploadSlideBackgroundImage.upload();
            if (!slideBckImageUrl) {
                snackbar.error('Error uploading slide background image!');
            }
            cSlideBckStyles.backgroundImageUrl = slideBckImageUrl!;
        }

        const quizStyles: QuizStyles = {
            titlePageStyles: {
                title: titleFontStyles,
                ...cTitleBckStyles
            },
            backgroundStyles: cSlideBckStyles,
            fontStyles: questionFontStyles
        };

        if (props.quiz.styles?.id) {
            await updateQuizStylesMutation({
                variables: {
                    stylesId: props.quiz.styles?.id!,
                    ...quizStyles
                }
            });
            return;
        }

        await upsertQuizStylesMutation({
            variables: {
                quizId: props.quiz.id!,
                ...quizStyles
            }
        });
    };

    const getUploadTitleBckImage = useCallback((action?: () => Promise<string | undefined>) => {
        setUploadTitlePageBackgroundImage(action ? { upload: action } : undefined);
    }, []);

    const getUploadSlideBckImage = useCallback((action?: () => Promise<string | undefined>) => {
        setUploadSlideBackgroundImage(action ? { upload: action } : undefined);
    }, []);

    return (
        <>
            <h5>Title page styles</h5>
            <Typography variant="h5" sx={{ textAlign: 'left', paddingLeft: 5, paddingBottom: 3, fontWeight: 600 }}>
                Background
            </Typography>
            <EditBackgroundStyles
                backgroundStyles={titleBackgroudStyles}
                onBackgroundStylesChange={onTitleBackgroundStylesChange}
                getUploadBackgroundImageAction={getUploadTitleBckImage}
            />
            <Typography variant="h5" sx={{ textAlign: 'left', padding: 5, fontWeight: 600 }}>
                Font styles
            </Typography>
            <EditFontStyles fontStyles={titleFontStyles} onFontStylesChange={onTitleFontStylesChange} />
            <hr />
            <h5>Slide styles</h5>
            <Typography variant="h5" sx={{ textAlign: 'left', paddingLeft: 5, paddingBottom: 3, fontWeight: 600 }}>
                Background
            </Typography>
            <EditBackgroundStyles
                backgroundStyles={questionBackgroudStyles}
                onBackgroundStylesChange={onBackgroundStylesChange}
                getUploadBackgroundImageAction={getUploadSlideBckImage}
            />
            <Typography variant="h5" sx={{ textAlign: 'left', padding: 5, fontWeight: 600 }}>
                Font styles
            </Typography>
            <EditFontStyles fontStyles={questionFontStyles} onFontStylesChange={onFontStylesChange} />

            <Button variant="contained" onClick={onSaveStyles} disabled={saving}>
                Save styles
            </Button>
            {saving && <Spinner />}
        </>
    );
};
